import React, { useState, useEffect, useCallback } from 'react';
import axios, { API } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Calendar, TrendingUp, CheckCircle, Archive } from 'lucide-react';
import { toast } from 'sonner';

const ArchivedGoalCard = ({ goal, onUnarchive }) => {
  const progress = goal.total_tasks > 0 ? (goal.completed_tasks / goal.total_tasks) * 100 : 0;

  return (
    <Card className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-amber-500 bg-amber-50/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center">
              <Archive className="mr-2 h-4 w-4 text-amber-600" />
              {goal.title}
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
          </div>
          <Badge variant="secondary" className="bg-amber-100 text-amber-800">Archived</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Progress</span>
              <span className="font-medium text-justb-primary">{goal.completed_tasks}/{goal.total_tasks} tasks</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span className="flex items-center">
              <Calendar className="mr-1 h-4 w-4" />
              Target: {new Date(goal.target_date).toLocaleDateString()}
            </span>
            <span className="flex items-center">
              <TrendingUp className="mr-1 h-4 w-4" />
              {goal.extension_count} extensions
            </span>
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUnarchive(goal.id)}
              className="text-justb-secondary hover:text-justb-secondary hover:bg-justb-secondary/10"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Restore Goal
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ArchivedGoalsTab = ({ onGoalUpdate }) => {
  const [archivedGoals, setArchivedGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchArchivedGoals = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/goals/archived`);
      setArchivedGoals(response.data);
    } catch {
      toast.error('Failed to load archived goals');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArchivedGoals();
  }, [fetchArchivedGoals]);

  const handleUnarchiveGoal = async (goalId) => {
    try {
      await axios.post(`${API}/goals/${goalId}/unarchive`);
      toast.success('Goal restored successfully');
      fetchArchivedGoals();
      onGoalUpdate();
    } catch {
      toast.error('Failed to restore goal');
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-justb-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading archived goals...</p>
      </div>
    );
  }

  if (archivedGoals.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Archive className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold mb-2 text-gray-900">No Archived Goals</h3>
          <p className="text-gray-600 mb-6">Goals you archive will appear here for easy restoration</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {archivedGoals.map((goal) => (
          <ArchivedGoalCard key={goal.id} goal={goal} onUnarchive={handleUnarchiveGoal} />
        ))}
      </div>
    </div>
  );
};

export default ArchivedGoalsTab;
